/**
 * 全局答题 store。
 *
 * 原站是一个外部 store + useSyncExternalStore 的模式，方法名实测为
 * reset / init / sync / completeGuide / submitQuest —— 这里保持一致，
 * 便于和线上行为逐项对照。
 */
import { useSyncExternalStore } from 'react';
import { fetchDetail, fetchMeta, fetchStatus, submitQuest } from './api';
import type { AnswerKey, DetailData, MetaData, MyResult, Selection } from '../types';
import { COPY } from '../types';
import { toast } from './toast';

export interface QuizState {
  story: DetailData['story'] | null;
  flow: DetailData['flow'];
  /**
   * 原站语义：只有 /status 返回 code 0 时才置 true。
   * 未接角色时内容区不渲染任何东西（只剩背景与顶部栏）。
   */
  ready: boolean;
  /** qId -> 't' | 'f' */
  selection: Selection;
  myResult: MyResult | null;
  /** 最后一段 guide 完成后置位，切换到结果盘面 */
  end: boolean;
  visitedGuides: string[];
  meta: MetaData | null;
  /**
   * 首屏状态机（对齐原站根组件）：
   *  null     —— /meta 还没回答，只有背景与音轨
   *  loading  —— /meta 返回 pass 为假，走加载页（带 progress）
   *  quest    —— /meta 返回 pass 为真，进入答题
   */
  phase: null | 'loading' | 'quest';
  /** 加载页用的进度值（来自 /meta 的 data.progress） */
  metaProgress: number;
  error: string | null;
  loading: boolean;
}

const initialState: QuizState = {
  story: null,
  flow: [],
  ready: false,
  selection: {},
  myResult: null,
  end: false,
  visitedGuides: [],
  meta: null,
  phase: null,
  metaProgress: 0,
  error: null,
  loading: false,
};

let state: QuizState = initialState;
const listeners = new Set<() => void>();

function setState(patch: Partial<QuizState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => state;

export function useQuiz<T>(selector: (s: QuizState) => T): T {
  return useSyncExternalStore(
    subscribe,
    () => selector(state),
    () => selector(initialState),
  );
}

/** 订阅整个 store（原站的组件普遍这么用） */
export function useQuizState(): QuizState {
  return useSyncExternalStore(subscribe, getSnapshot, () => initialState);
}

/* -------------------------------------------------------------------------- */
/*  actions —— 与线上同名                                                     */
/* -------------------------------------------------------------------------- */

export const actions = {
  /** 原站：t({visitedGuides:[],ready:false,selection:{},myResult:null,end:false}) */
  reset() {
    setState({ visitedGuides: [], ready: false, selection: {}, myResult: null, end: false });
  },

  /**
   * 根组件挂载时请求 /meta，决定进入加载页还是答题页（原站行为）。
   * 与原站一致：code 非 0 时只弹 toast，页面停留在空白态。
   */
  async boot() {
    try {
      const meta = await fetchMeta();
      if (meta.code !== 0) {
        setState({ error: meta.msg || COPY.requestError });
        return;
      }
      setState({
        meta: meta.data,
        metaProgress: meta.data.progress ?? 0,
        phase: meta.data.pass ? 'quest' : 'loading',
        error: null,
      });
    } catch (e) {
      setState({ error: e instanceof Error ? e.message : String(e) });
    }
  },

  /**
   * 直接进入答题页。
   * 原站只有在 /meta 返回 pass 为真时才会走到这里；离线（数据来自本地快照）时
   * 由根组件在加载页播完后调用，避免本地打开永远停在加载页。
   */
  enterQuest() {
    setState({ phase: 'quest' });
  },

  /** 进入答题页后加载剧情（原站 Et 的 useEffect(() => store.init())） */
  async init() {
    if (state.loading) return;
    setState({ loading: true });
    try {
      const detail = await fetchDetail();
      if (detail.code !== 0) {
        setState({ loading: false, error: detail.msg || COPY.requestError });
        return;
      }
      setState({ story: detail.data.story, flow: detail.data.flow, loading: false });
    } catch (e) {
      setState({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  /**
   * GET /status —— 这是 ready 与 end 的唯一来源（原站行为）：
   *   code 0  -> 合并服务端返回的 selection，ready:true，end = ('myResult' in data)
   *   code -1 且 reason === 'NO_ROLE' -> 若此前是 ready，则 reset()
   */
  async sync() {
    try {
      const res = await fetchStatus();
      if (res.code === 0 && res.data) {
        const data = res.data;
        setState({
          selection: data.selection ?? {},
          myResult: data.myResult ?? null,
          visitedGuides: data.visitedGuides ?? [],
          ready: true,
          // 关键：以"字段是否存在"为准，即使值为 null 也算已出结果
          end: Object.prototype.hasOwnProperty.call(data, 'myResult'),
        });
        return;
      }
      if (res.code === -1 && res.data?.reason === 'NO_ROLE') {
        if (state.ready) actions.reset();
        if (res.msg) toast(res.msg);
        return;
      }
      if (res.msg) toast(res.msg);
    } catch {
      /* 静默：拉取失败不打断答题 */
    }
  },

  /** 原站：end = gId === flow[flow.length-1].key */
  completeGuide(gId: string) {
    const visited = [...state.visitedGuides, gId];
    const last = state.flow[state.flow.length - 1]?.key;
    setState({ visitedGuides: visited, end: gId === last });
  },

  /**
   * POST /submit -> { qId, aId }
   * 原站只更新 selection 与 myResult，**不会**置 end。
   */
  async submitQuest(qId: string, aId: AnswerKey) {
    const total = state.story?.questions.length ?? 13;
    const res = await submitQuest(qId, aId, { selection: state.selection, total });
    if (res.code === 0) {
      setState({
        selection: res.data.selection,
        myResult: res.data.myResult ?? null,
      });
    } else if (res.msg) {
      toast(res.msg);
    }
    return res;
  },

  /** 重新开始：清空选择并回到第一题 */
  restart() {
    setState({ visitedGuides: [], selection: {}, myResult: null, end: false });
  },
};
